import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { defaultHome } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import BackLink from "@/components/BackLink";

const HERO_IMG = "/login-hero.png";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";
const fieldClass =
    "h-11 border-[#E8E4DE] bg-[#FFFEF9] pl-11 text-sm font-medium text-[#2D2926] shadow-sm placeholder:text-[#9A9088] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";
const labelClass = "text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]";

function formatErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(", ");
    return d.msg || JSON.stringify(d);
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
            <span className="px-2.5 text-[10px] leading-none text-[#C9A227]">◆</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
        </div>
    );
}

export default function Login() {
    const [email, setEmail] = useState("owner@demosalon.com");
    const [password, setPassword] = useState("Admin@123");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const data = await login(email, password);
            toast.success("Welcome back");
            if (data?.role === "super_admin") navigate("/platform");
            else navigate(`/app/${defaultHome(data.role)}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(detail != null ? formatErr(detail) : (err.message || "Something went wrong"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif] lg:grid lg:grid-cols-2">
            {/* Left — hero image */}
            <div className="relative h-[32vh] min-h-[220px] lg:sticky lg:top-0 lg:h-screen lg:min-h-0">
                <img
                    src={HERO_IMG}
                    alt="Salon model with flowing hair"
                    className="absolute inset-0 h-full w-full object-cover object-[72%_center]"
                />
                <div
                    className="absolute inset-0 lg:hidden"
                    style={{
                        background:
                            "linear-gradient(to bottom, transparent 40%, rgba(250,249,246,0.85) 92%, #FAF9F6 100%)",
                    }}
                    aria-hidden
                />
                <div
                    className="absolute inset-y-0 right-0 hidden w-20 lg:block"
                    style={{
                        background: "linear-gradient(to right, transparent, rgba(250,249,246,0.65))",
                    }}
                    aria-hidden
                />
                {/* Single bottom scrim — text only, no boxes */}
                <div
                    className="absolute inset-x-0 bottom-0 hidden h-[52%] lg:block"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(45,8,15,0.88) 0%, rgba(45,8,15,0.5) 42%, transparent 100%)",
                    }}
                    aria-hidden
                />

                <div className="relative hidden h-full flex-col p-10 xl:p-14 lg:flex">
                    <Link
                        to="/"
                        className="w-fit rounded-lg bg-[#FAF9F6]/95 px-4 py-2.5 shadow-[0_2px_20px_rgba(45,41,38,0.1)] backdrop-blur-sm transition-opacity hover:opacity-90"
                    >
                        <BrandLogo
                            size={34}
                            textClass="font-serif text-xl font-semibold text-[#4A0E17]"
                            accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                        />
                    </Link>

                    <div className="mt-auto max-w-[22rem]">
                        <div className="mb-4 h-px w-10 bg-[#D4AF37]" aria-hidden />
                        <p
                            className={`${EYEBROW} text-[#D4AF37]`}
                            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
                        >
                            Salon &amp; spa platform
                        </p>
                        <p
                            className="mt-3 font-serif text-[1.65rem] font-normal leading-[1.35] tracking-tight text-white xl:text-[1.85rem]"
                            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
                        >
                            <span className="font-medium text-[#E8C96A]">Five-star</span> salon management
                            — calendar, GST billing &amp; reports in one place.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right — sign in */}
            <div className="relative flex flex-1 justify-center px-6 py-10 sm:px-10 lg:py-12">
                <div
                    className="pointer-events-none absolute inset-0 opacity-50"
                    style={{
                        background:
                            "radial-gradient(ellipse 70% 60% at 80% 20%, rgba(212,175,55,0.08), transparent)",
                    }}
                    aria-hidden
                />

                <div className="relative w-full max-w-[440px]">
                    <BackLink className="mb-5" data-testid="login-back-home" />

                    <div className="mb-7 lg:mb-8">
                        <Link to="/" className="inline-block transition-opacity hover:opacity-90 lg:hidden">
                            <BrandLogo
                                size={36}
                                textClass="font-serif text-2xl font-semibold text-[#4A0E17]"
                                accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                            />
                        </Link>
                        <div className="hidden lg:block">
                            <p className={EYEBROW} style={{ color: "#C9A227" }}>
                                Welcome back
                            </p>
                            <h1 className="mt-2 font-serif text-[2.125rem] font-semibold leading-tight tracking-tight text-[#4A0E17]">
                                Sign in
                            </h1>
                            <GoldDivider className="mt-4" />
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                        <div className="text-center lg:hidden">
                            <h1 className="font-serif text-3xl font-semibold tracking-tight text-[#4A0E17]">
                                Sign in
                            </h1>
                            <GoldDivider className="mx-auto mt-4" />
                            <p className="mt-3 text-sm font-medium text-[#8A8178]">
                                Your salon dashboard · Sign in to continue
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className={labelClass}>
                                    Email address
                                </Label>
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className={fieldClass}
                                        placeholder="you@yoursalon.com"
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <Label htmlFor="password" className={labelClass}>
                                        Password
                                    </Label>
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs font-semibold text-[#C84B31] hover:underline"
                                        data-testid="login-forgot-password"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                    <Input
                                        id="password"
                                        type={showPw ? "text" : "password"}
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className={`${fieldClass} pr-12`}
                                        placeholder="Enter your password"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#8A8178] transition-colors hover:text-[#4A0E17]"
                                        onClick={() => setShowPw((v) => !v)}
                                        aria-label={showPw ? "Hide password" : "Show password"}
                                        tabIndex={-1}
                                    >
                                        {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={busy}
                            className="h-12 w-full rounded-lg bg-[#4A0E17] text-sm font-semibold tracking-wide shadow-[0_6px_24px_rgba(74,14,23,0.25)] transition-all hover:bg-[#3a0b12] hover:shadow-[0_8px_28px_rgba(74,14,23,0.32)]"
                            data-testid="login-submit-button"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>

                        <p className="text-center text-sm font-medium text-[#8A8178]">
                            New here?{" "}
                            <Link
                                to="/register"
                                className="font-semibold text-[#C84B31] underline-offset-4 hover:underline"
                            >
                                Start your free trial
                            </Link>
                        </p>

                        <div className="rounded-xl border border-[#E8E4DE] bg-white px-4 py-3 text-center text-xs font-medium text-[#8A8178] shadow-sm">
                            <span className="font-bold uppercase tracking-wider text-[#9A7B10]">Demo</span>
                            <span className="mx-1.5 text-[#E8E4DE]">|</span>
                            <span className="text-[#3D2817]">owner@demosalon.com</span>
                            <span className="mx-1 text-[#E8E4DE]">/</span>
                            <span className="text-[#3D2817]">Admin@123</span>
                        </div>
                    </form>

                    <p className="mt-8 text-center text-[11px] font-medium tracking-wide text-[#9A9088]">
                        Secured · Private to your salon
                    </p>
                </div>
            </div>
        </div>
    );
}
